import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { email, form, FormField, minLength, required, validate } from "@angular/forms/signals";
import { Router } from "@angular/router";
import {
  AccessLevel,
  Currency,
  Customer,
  DspAdvertiserRequest,
  DspAdvertiserRequestCreate,
  DspAmazonAdvertiserCountry,
  DspIndustry,
  Marketplace,
  User,
} from "@front/m19-api-client";
import { CurrencySymbols, Marketplaces } from "@front/m19-models";
import { AccountMarketplaceService, AccountSelectionService, AuthService, BillingService } from "@front/m19-services";
import {
  IButtonDirective,
  IErrorComponent,
  IFormFieldComponent,
  IHintDirective,
  IIconComponent,
  IInputGroupDirective,
  IInputGroupInput,
  ILabelComponent,
  ISelectMenuImports,
  IStepperComponent,
  IToggleComponent,
  ToasterService,
  ITooltipDirective,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { FormNavigationService, IFormStep } from "@m19-board/shared/form-navigation/form-navigation.service";
import { FormShellComponent } from "@m19-board/shared/form-shell/form-shell.component";
import { validateUrl } from "@m19-board/utils/validators/urlValidators";
import { DspAdvertiserRequestService } from "libs/m19-services/src/lib/m19-services/dspAdvertiserRequest.service";
import { IPhoneInputComponent } from "libs/m19-ui/src/ui-components/iphone-input/iphone-input.component";
import { combineLatest, map, Observable } from "rxjs";

const currencyLabel = (currency: Currency) => `${currency} (${CurrencySymbols[currency]})`;

type BillingDetails = {
  contactName: string;
  companyName: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  addressCountry: string;
  city: string;
  state: string;
  postalCode: string;
  email: string;
  vat: string;
};

type DspAdvertiserRequestFormModel = {
  advertiser: {
    advertiserName: string;
    url: string;
    industry: DspIndustry | null;
    businessRegistrationNumber: string;
    country: string;
    timezone: string;
  };
  permissions: {
    grantedEmails: string[];
  };
  billing: BillingDetails & {
    currency: Currency;
  };
};

const EMPTY_BILLING_DETAILS: BillingDetails = {
  contactName: "",
  companyName: "",
  phone: "",
  addressLine1: "",
  addressLine2: "",
  addressCountry: "",
  city: "",
  state: "",
  postalCode: "",
  email: "",
  vat: "",
};

@Component({
  selector: "app-dsp-advertiser-request-page",
  providers: [FormNavigationService],
  imports: [
    IFormFieldComponent,
    IInputGroupDirective,
    IInputGroupInput,
    ILabelComponent,
    IHintDirective,
    ISelectMenuImports,
    IButtonDirective,
    IPhoneInputComponent,
    DatePipe,
    TranslocoDirective,
    IToggleComponent,
    IErrorComponent,
    IIconComponent,
    ITooltipDirective,
    IStepperComponent,
    FormField,
    FormShellComponent,
  ],
  templateUrl: "./dsp-advertiser-request-page.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspAdvertiserRequestPageComponent implements OnInit {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly dspAdvertiserRequestService = inject(DspAdvertiserRequestService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly billingService = inject(BillingService);
  private readonly accountMarketplaceService = inject(AccountMarketplaceService);
  private readonly formNavigationService = inject(FormNavigationService<DspAdvertiserRequestFormModel>);

  private readonly dspAdvertiserRequest$: Observable<DspAdvertiserRequest | undefined> =
    this.dspAdvertiserRequestService.dspAdvertiserRequest$;

  private readonly customer$ = combineLatest([
    this.billingService.organizations$,
    this.accountSelectionService.singleAccountMarketplaceSelection$,
  ]).pipe(
    map(([organizations, selection]) => {
      const organization = organizations.find((organization) => organization.id === selection.resourceOrganizationId);
      if (organization?.accessLevel === AccessLevel.ADMIN) return organization?.customer;
      return undefined;
    }),
  );
  readonly customer = toSignal(this.customer$, { requireSync: true });
  private readonly amazonUserEmails$: Observable<string[]> = combineLatest([
    this.accountMarketplaceService.accountMarketplaces$,
    this.accountSelectionService.singleAccountMarketplaceSelection$,
  ]).pipe(
    map(([accountMarketplaces, selection]) => {
      const amazonUserAccess = accountMarketplaces
        .find((am) => am.accountId === selection.accountId && am.marketplace === selection.marketplace)
        ?.amazonUserAccess?.filter((access) => access.hasAccessToProfile && access.isValidToken && !!access.email);
      return [...new Set(amazonUserAccess?.map((access) => access.email!) ?? [])];
    }),
  );
  private readonly user$ = this.authService.loggedUser$;
  private readonly user = toSignal(this.user$, { requireSync: true });
  private readonly selectedAccountMarketplace = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$,
    {
      requireSync: true,
    },
  );

  public readonly dspAdvertiserRequest = toSignal(this.dspAdvertiserRequest$);
  private readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });
  readonly isAdmin = computed(() => this.accountMarketplace().accessLevel === AccessLevel.ADMIN);

  readonly countries = toSignal(this.dspAdvertiserRequestService.dspAdvertiserCountries$, {
    initialValue: [] as DspAmazonAdvertiserCountry[],
  });

  private readonly formModel = signal<DspAdvertiserRequestFormModel>({
    advertiser: {
      advertiserName: this.selectedAccountMarketplace()?.accountName ?? "",
      url: "",
      industry: null,
      businessRegistrationNumber: "",
      country: "",
      timezone: "",
    },
    permissions: { grantedEmails: [] },
    billing: {
      ...EMPTY_BILLING_DETAILS,
      currency: this.user()?.defaultCurrency ?? Currency.EUR,
    },
  });

  readonly form = form(
    this.formModel,
    (schema) => {
      required(schema.advertiser.advertiserName);
      required(schema.advertiser.url);
      validate(schema.advertiser.url, validateUrl);
      required(schema.advertiser.industry);
      required(schema.advertiser.country);
      required(schema.advertiser.timezone);
      minLength(schema.permissions.grantedEmails, 1);
      required(schema.billing.currency);
      required(schema.billing.contactName);
      required(schema.billing.companyName);
      required(schema.billing.phone);
      required(schema.billing.addressLine1);
      required(schema.billing.addressCountry);
      required(schema.billing.city);
      required(schema.billing.postalCode);
      required(schema.billing.email);
      email(schema.billing.email);
      required(schema.billing.state, {
        when: ({ valueOf }) => this.statesOf(valueOf(schema.billing.addressCountry)).length > 0,
      });
    },
    {
      submission: {
        action: async () => this.submitRequest(),
      },
    },
  );

  readonly countrySearch = signal<string | undefined>("");
  readonly stateSearch = signal<string | undefined>("");

  readonly selectedCountry = computed<DspAmazonAdvertiserCountry | undefined>(() =>
    this.countries().find((country) => country.code === this.form.billing.addressCountry().value()),
  );
  readonly states = computed(() => this.selectedCountry()?.states ?? []);

  readonly filteredCountries = computed(() => {
    const query = this.countrySearch()?.trim().toLowerCase();
    if (!query) return this.countries();
    return this.countries().filter((c) => c.name.toLowerCase().includes(query) || c.code.toLowerCase().includes(query));
  });

  readonly filteredStates = computed(() => {
    const query = this.stateSearch()?.trim().toLowerCase();
    if (!query) return this.states();
    return this.states().filter((state) => state.toLowerCase().includes(query));
  });

  readonly selectedCurrencyLabel = computed(() => currencyLabel(this.form.billing.currency().value()));
  readonly formSubmitLabel = computed(() =>
    this.dspAdvertiserRequest()
      ? this.translocoService.translate("dsp-advertiser-request-page.update_request")
      : this.translocoService.translate("dsp-advertiser-request-page.submit_request"),
  );
  readonly submitLoading = signal(false);

  readonly currentStepId = this.formNavigationService.currentStepId;
  readonly formSteps = this.formNavigationService.formSteps;
  readonly isFirstStep = this.formNavigationService.isFirstStep;
  readonly isLastStep = this.formNavigationService.isLastStep;
  readonly canGoNext = this.formNavigationService.canGoNext;
  readonly industries: DspIndustry[] = Object.values(DspIndustry);
  readonly currencies = Object.values(Currency).map((currency) => ({
    value: currency,
    label: currencyLabel(currency),
  }));
  readonly autofillFromBillingInfoChecked = signal(true);
  readonly isAddingGrantedEmail = signal(false);

  private readonly grantedEmailModel = signal("");
  readonly grantedEmailForm = form(this.grantedEmailModel, (schema) => {
    required(schema);
    email(schema);
  });

  constructor() {
    combineLatest([this.accountSelectionService.singleAccountMarketplaceSelection$, this.amazonUserEmails$])
      .pipe(takeUntilDestroyed())
      .subscribe(([selection, amazonUserEmails]) => {
        const marketplace: Marketplace = selection.marketplace;
        this.formModel.update((model) => ({
          ...model,
          advertiser: {
            advertiserName: selection.accountName,
            url: "",
            industry: null,
            businessRegistrationNumber: "",
            country: marketplace,
            timezone: Marketplaces[marketplace].timeZone,
          },
          permissions: {
            grantedEmails: amazonUserEmails.length > 0 ? amazonUserEmails : this.defaultGrantedEmails(),
          },
        }));
      });

    combineLatest([this.customer$, this.user$, this.dspAdvertiserRequest$])
      .pipe(takeUntilDestroyed())
      .subscribe(([customer, user, request]) => {
        if (request) {
          this.fillFormFromRequest(request);
          this.formNavigationService.updateReachedStepIds(["advertiser", "permissions", "billing"]);
          return;
        }
        if (customer && user) {
          this.fillFormFromCustomer(customer, user);
        }
      });
  }

  ngOnInit(): void {
    this.formNavigationService.configure({
      form: this.form,
      blocked: this.submitLoading,
      steps: [
        { stepId: "advertiser", label: this.translocoService.translate("dsp-advertiser-request-page.advertiser_info") },
        {
          stepId: "permissions",
          label: this.translocoService.translate("dsp-advertiser-request-page.permitted_users"),
        },
        { stepId: "billing", label: this.translocoService.translate("dsp-advertiser-request-page.billing_details") },
      ],
    });
  }

  goToStep(stepId: IFormStep<DspAdvertiserRequestFormModel>) {
    this.formNavigationService.goToStep(stepId);
  }

  back() {
    this.formNavigationService.back();
  }

  next() {
    if (this.isLastStep() && !this.isAdmin()) return;
    this.formNavigationService.next();
  }

  cancel() {
    this.router.navigate(["/advertising/dsp/strategies"]);
  }

  private submitRequest() {
    if (this.form().invalid() || !this.isAdmin()) return;
    this.submitLoading.set(true);

    const request: DspAdvertiserRequestCreate = this.getDspAdvertiserRequestFormValue();

    if (this.dspAdvertiserRequest()) {
      this.updateDspAdvertiserRequest(request);
      return;
    }

    this.dspAdvertiserRequestService
      .createDspAdvertiserRequest(
        this.accountMarketplace().accountId,
        this.accountMarketplace().marketplace,
        this.accountMarketplace().resourceOrganizationId,
        request,
      )
      .subscribe({
        next: () => {
          this.router.navigate(["/advertising/dsp/stats"]);
          this.toastrService.success("DSP Advertiser request submitted successfully");
          this.submitLoading.set(false);
        },
        error: (e) => {
          this.submitLoading.set(false);
          this.toastrService.error("Failed to submit DSP Advertiser request", e);
        },
      });
  }

  toggleAutofillAndFillFormFromCustomer() {
    this.autofillFromBillingInfoChecked.set(!this.autofillFromBillingInfoChecked());
    if (this.autofillFromBillingInfoChecked()) {
      this.fillFormFromCustomer(this.customer()!, this.user()!);
    } else {
      this.formModel.update((model) => ({ ...model, billing: { ...model.billing, ...EMPTY_BILLING_DETAILS } }));
    }
  }

  selectAddressCountry(countryCode: string) {
    this.form.billing.addressCountry().value.set(countryCode);
    if (!this.statesOf(countryCode).includes(this.form.billing.state().value())) {
      this.form.billing.state().value.set("");
    }
  }

  removeGrantedEmail(grantedEmail: string) {
    this.form.permissions
      .grantedEmails()
      .value.update((grantedEmails) => grantedEmails.filter((e) => e !== grantedEmail));
  }

  addGrantedEmail() {
    if (this.grantedEmailForm().invalid()) {
      this.grantedEmailForm().markAsDirty();
      this.grantedEmailForm().markAsTouched();
      return;
    }
    const grantedEmail = this.grantedEmailModel().trim();
    if (!this.form.permissions.grantedEmails().value().includes(grantedEmail)) {
      this.form.permissions.grantedEmails().value.update((grantedEmails) => [...grantedEmails, grantedEmail]);
    }
    this.cancelGrantedEmail();
  }

  cancelGrantedEmail() {
    this.grantedEmailForm().reset("");
    this.isAddingGrantedEmail.set(false);
  }

  private defaultGrantedEmails(): string[] {
    const email = this.user()?.email;
    return email ? [email] : [];
  }

  private statesOf(countryCode: string): string[] {
    return this.countries().find((country) => country.code === countryCode)?.states ?? [];
  }

  private getDspAdvertiserRequestFormValue(): DspAdvertiserRequestCreate {
    const { advertiser, permissions, billing } = this.form().value();
    return {
      ...advertiser,
      ...permissions,
      ...billing,
      industry: advertiser.industry!,
      addressLine2: billing.addressLine2 || undefined,
      businessRegistrationNumber: advertiser.businessRegistrationNumber || undefined,
      vat: billing.vat || undefined,
      requestedBy: this.user()!.email!,
    };
  }

  private fillFormFromCustomer(customer: Customer, user: User) {
    this.formModel.update((model) => ({
      ...model,
      billing: { ...model.billing, ...this.getBillingDetailsFromCustomer(customer, user) },
    }));
  }

  private fillFormFromRequest(request: DspAdvertiserRequest) {
    this.formModel.set({
      advertiser: {
        advertiserName: request.advertiserName,
        url: request.url,
        industry: request.industry,
        businessRegistrationNumber: request.businessRegistrationNumber ?? "",
        country: request.country,
        timezone: request.timezone,
      },
      permissions: { grantedEmails: request.grantedEmails ?? [] },
      billing: {
        currency: request.currency,
        contactName: request.contactName,
        companyName: request.companyName,
        phone: request.phone,
        addressLine1: request.addressLine1,
        addressLine2: request.addressLine2 ?? "",
        addressCountry: request.addressCountry,
        city: request.city,
        state: request.state,
        postalCode: request.postalCode,
        email: request.email,
        vat: request.vat ?? "",
      },
    });
  }

  private updateDspAdvertiserRequest(request: DspAdvertiserRequestCreate) {
    if (this.dspAdvertiserRequest()) {
      this.dspAdvertiserRequestService
        .updateDspAdvertiserRequest(
          this.accountMarketplace()!.accountId,
          this.accountMarketplace()!.marketplace,
          this.accountMarketplace()!.resourceOrganizationId,
          request,
        )
        .subscribe({
          next: () => {
            this.router.navigate(["/advertising/dsp/stats"]);
            this.toastrService.success("DSP Advertiser request updated successfully");
            this.submitLoading.set(false);
          },
          error: (e) => {
            this.submitLoading.set(false);
            this.toastrService.error("Failed to update DSP Advertiser request", e);
          },
        });
    }
  }

  private getBillingDetailsFromCustomer(customer: Customer, user: User): BillingDetails {
    return {
      contactName: (user.firstName ?? "") + " " + (user.lastName ?? ""),
      companyName: customer.name ?? "",
      addressLine1: customer.addressLine1 ?? "",
      addressLine2: customer.addressLine2 ?? "",
      addressCountry: customer.country ?? "",
      city: customer.city ?? "",
      state: customer.state ?? "",
      postalCode: customer.postalCode ?? "",
      phone: user.phoneNumber ?? "",
      email: user.email ?? "",
      vat: customer.vat ?? "",
    };
  }
}
